import { Aperture, ArrowUp, CircleUserRound, MapPin, Settings2, ShoppingBag, X } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { api } from '../lib/api'
import { GuideSyncControl } from './GuideSyncControl'
import { guideDisplayKey, guideDisplaySchema, type GuideDisplaySettings } from '../lib/guide-display-settings'

export function GuideStatusBar({ settings, editable, edit }: { settings: GuideDisplaySettings; editable: boolean; edit: () => void }) {
  const batteryClipId = useId()
  const battery = Number.isFinite(settings.battery) ? Math.max(0, Math.min(100, Math.round(settings.battery))) : 0
  return <div className="guide-status-frame"><div className="guide-status-bar" aria-label={`展示时间 ${settings.time}，电量 ${battery}%`}>
    <span className="guide-status-time">{settings.time}</span>
    <div className="guide-status-icons" aria-hidden="true">
      <svg className="guide-ios-cellular" viewBox="0 0 22 16" fill="currentColor">
        <rect x="0" y="5.2" width="3.8" height="5" rx="1.9"/><rect x="6" y="3.5" width="3.8" height="6.7" rx="1.9"/><rect x="12" y="1.7" width="3.8" height="8.5" rx="1.9"/><rect x="18" y="0" width="3.8" height="10.2" rx="1.9"/>
        <rect x="0" y="12" width="3.8" height="4" rx="1.9"/><rect x="6" y="12" width="3.8" height="4" rx="1.9"/><rect x="12" y="12" width="3.8" height="4" rx="1.9"/><rect x="18" y="12" width="3.8" height="4" rx="1.9"/>
      </svg>
      <svg className="guide-ios-wifi" viewBox="0 0 21 16" fill="currentColor">
        <path d="M.8 4.55a14.65 14.65 0 0 1 19.4 0c.6.52.63 1.43.08 1.99-.53.54-1.36.57-1.94.07a11.85 11.85 0 0 0-15.68 0c-.58.5-1.41.47-1.94-.07a1.37 1.37 0 0 1 .08-1.99Z"/>
        <path d="M4.09 8.11a9.7 9.7 0 0 1 12.82 0c.56.5.6 1.36.08 1.9-.5.53-1.31.58-1.88.12a6.95 6.95 0 0 0-9.22 0c-.57.46-1.38.41-1.88-.12a1.32 1.32 0 0 1 .08-1.9Z"/>
        <path d="M7.82 12.01a4.14 4.14 0 0 1 5.36 0c.36.3.39.83.06 1.17l-2.1 2.17a.9.9 0 0 1-1.28 0l-2.1-2.17a.8.8 0 0 1 .06-1.17Z"/>
      </svg>
      <span className={`guide-battery${battery===100?' guide-battery-full':''}`} data-battery-level={battery}>
        <svg viewBox="0 0 32 16" fill="currentColor">
          <defs><clipPath id={batteryClipId}><rect width={28.7 * battery / 100} height="16"/></clipPath></defs>
          <rect x="0" y="0" width="28.7" height="15.65" rx="4.9" fill="#b2b2b2"/>
          <rect className="guide-battery-fill" x="0" y="0" width="28.7" height="15.65" rx="4.9" clipPath={`url(#${batteryClipId})`}/>
          <path opacity=".3" d="M29.5 5a3.2 3.2 0 0 1 0 6Z"/>
        </svg>
        <b>{battery}</b>
      </span>
    </div>
    {editable && <button className="guide-status-edit" onClick={edit} aria-label="修改状态栏和屏幕宽度" title="修改时间、电量和屏幕宽度"><Settings2 size={14}/> 修改状态栏</button>}
  </div></div>
}

export function GuideBottomBar() {
  return <footer className="guide-bottom-bar" aria-label="展示页面底栏">
    <div className="guide-bottom-items">
      <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}><ArrowUp/><span>回顶部</span></button>
      <span className="guide-bottom-display"><MapPin/><span>附近</span></span>
      <span className="guide-bottom-display"><ShoppingBag/><span>红包</span></span>
      <span className="guide-bottom-display"><Aperture/><span>发圈</span></span>
      <span className="guide-bottom-display"><CircleUserRound/><span>我的</span></span>
    </div>
    <div className="guide-home-area" aria-hidden="true"><span/></div>
  </footer>
}

export function GuideDisplayEditor({ settings, close, saved, productsUpdated, saveOverride }: { settings: GuideDisplaySettings; close: () => void; saved: (value: GuideDisplaySettings) => void; productsUpdated: () => void; saveOverride?: (value: GuideDisplaySettings) => Promise<void> }) {
  const [draft, setDraft] = useState(settings)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const dialog = useRef<HTMLDialogElement>(null)
  useEffect(() => { dialog.current?.showModal() }, [])
  const save = async () => {
    const parsed = guideDisplaySchema.safeParse(draft)
    if (!parsed.success) { setError('时间请用 00:00–23:59，电量请填 0–100 的整数。'); return }
    setBusy(true); setError('')
    try {
      if (saveOverride) await saveOverride(parsed.data)
      else await api.saveSetting(guideDisplayKey, JSON.stringify(parsed.data))
      saved(parsed.data)
    }
    catch (reason) { setError(reason instanceof Error ? reason.message : '保存失败，请重试'); setBusy(false) }
  }
  return <dialog className="guide-display-editor" ref={dialog} aria-labelledby="guide-display-title" onCancel={event => { event.preventDefault(); if (!busy) close() }}>
    <form onSubmit={event => { event.preventDefault(); void save() }}>
      <header><h2 id="guide-display-title">状态栏与屏幕</h2><button type="button" onClick={close} disabled={busy} aria-label="关闭状态栏设置"><X size={20}/></button></header>
      <p>{saveOverride ? '本地测试：保存仅更新当前预览，不会修改线上网站。' : '用于展示和截图，不代表设备真实状态；保存后所有同事都能看到。'}</p>
      <label>显示时间<input type="text" required placeholder="10:55" maxLength={5} value={draft.time} onChange={e => setDraft(current => ({ ...current, time: e.target.value }))}/></label>
      <label>电量百分比<input type="number" required min="0" max="100" step="1" value={Number.isNaN(draft.battery) ? '' : draft.battery} onChange={e => setDraft(current => ({ ...current, battery: e.target.value === '' ? Number.NaN : Number(e.target.value) }))}/></label>
      <label>桌面展示宽度<select value={draft.width} onChange={e => setDraft({ ...draft, width: Number(e.target.value) as 393 | 430 })}><option value={393}>393px · 标准屏</option><option value={430}>430px · 大屏</option></select></label>
      <div className="guide-status-editor-preview"><span>即时预览 · 电量图形随数值变化</span><GuideStatusBar settings={draft} editable={false} edit={() => {}}/></div>
      {!saveOverride && <GuideSyncControl updated={productsUpdated}/>}
      {error && <p className="guide-display-error" role="alert">{error}</p>}
      <footer><button type="button" onClick={close} disabled={busy}>取消</button><button type="submit" disabled={busy}>{busy ? '保存中…' : '保存并立即显示'}</button></footer>
    </form>
  </dialog>
}
